import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoryAPI, collectionAPI, productAPI } from '../services/api';

const normalizeImageUrl = (url) => {
  if (!url) return '';
  const value = String(url).trim();
  const safeValue = value.replace(/\.heic(?=[?#]|$)/gi, '.jpg').replace(/\.heif(?=[?#]|$)/gi, '.jpg');
  return safeValue.includes('cloudinary.com') && !/[?&]fm=jpg/i.test(safeValue)
    ? `${safeValue}${safeValue.includes('?') ? '&' : '?'}fm=jpg`
    : safeValue;
};

const boyHeroImage = '/BirthdayWear.jpg';

const categoryImageMap = {
  'Birthday Wear': '/BirthdayWear.jpg',
  'Family Combo': '/familycombo.jpeg',
  'Women Party Wear': '/partywear.jpg',
  'Traditional Wear': '/traditional.jpg',
  'Boy/Men': '/boywear.jpeg',
  'Gender Reveal': '/GenderReveal.jpg'
};

const homeCategoryOrder = [
  { label: 'Birthday Wear', aliases: ['birthday wear', 'birthday outfits'] },
  { label: 'Family Combo', aliases: ['family combo', 'family combos'] },
  { label: 'Women Party Wear', categoryId: '6aa8f4c523f46f6ff43cb38c', aliases: ['women party wear', 'party wear'] },
  { label: 'Traditional Wear', aliases: ['traditional wear'] },
  { label: 'Boy/Men', aliases: ['boy/men', 'boy men', 'boys', 'men'] },
  { label: 'Gender Reveal', aliases: ['gender reveal'] }
];

export default function Home() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      productAPI.getNewProducts(),
      categoryAPI.getAllCategories(),
      collectionAPI.getFeaturedCollections()
    ]).then(([productResult, categoryResult, collectionResult]) => {
      if (productResult.status === 'fulfilled') setProducts(productResult.value.data.products || []);
      if (categoryResult.status === 'fulfilled') setCategories(categoryResult.value.data.categories || []);
      if (collectionResult.status === 'fulfilled') setCollections(collectionResult.value.data.collections || []);
    }).finally(() => setLoading(false));
  }, []);

  const formatPrice = (value) => new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0
  }).format(value || 0);

  const imageFor = (item, fallback) => normalizeImageUrl(
    item?.images?.find((image) => image.isPrimary)?.url || item?.images?.[0]?.url ||
    item?.image?.url || item?.image || fallback
  );
  const heroImage = '/BirthdayWear.jpg';
  const birthdayFeaturedName = 'Birthday Wear';
  const birthdayFeaturedPrice = 8000;
  const categoryItems = homeCategoryOrder.map(({ label, categoryId, aliases }) => {
    const match = categoryId
      ? categories.find((category) => category._id === categoryId)
      : categories.find((category) => aliases.includes(category.name.trim().toLowerCase()));
    return match ? { ...match, name: label } : categoryId ? { _id: categoryId, name: label } : label;
  });
  const displayedProducts = products.slice(0, 4);
  const displayedCollections = collections.slice(0, 2);

  if (loading) return <div className="page-loading home-loading">Preparing the collection...</div>;

  return (
    <div className="new-home">
      <div className="announcement-bar" aria-label="Store updates and pricing notice">
        <div className="announcement-track">
          {[
            'Urgent orders are also accepted — prices vary, confirm on WhatsApp before placing your order'
          ].map((item, index) => <span key={`${item}-${index}`}>{item}</span>)}
          {[
            'Urgent orders are also accepted — prices vary, confirm on WhatsApp before placing your order'
          ].map((item, index) => <span key={`repeat-${item}-${index}`}>{item}</span>)}
        </div>
      </div>
      <section className="new-hero home-band">
        <div className="new-hero-copy">
          <span className="new-kicker">Festive dressing, thoughtfully made</span>
          <h1>Matching moments for the people you love.</h1>
          <p>Handcrafted occasion wear for daughters, mothers, and every family memory worth dressing up for.</p>
          <div className="new-actions">
            <button className="new-button" onClick={() => navigate('/shop')}>Shop party wear <span>↗</span></button>
            <button className="new-button new-button-light" onClick={() => navigate('/contact')}>Book a fit consultation</button>
          </div>
          <div className="hero-proof"><span>✦</span> Made in India <span>•</span> Custom sizing available</div>
        </div>
        <div className="new-hero-media">
          <img src={heroImage} alt="Birthday Wear occasion look" />
          <div className="hero-product-tag"><span>Featured occasion look</span><strong>{birthdayFeaturedName}</strong><b>{formatPrice(birthdayFeaturedPrice)}</b></div>
        </div>
      </section>
      <section className="trust-strip home-band">
        {[
          ['▥', 'Custom Fit for Both', 'Mom XS–3XL & Daughter 0–14 Years', 'fit'],
          ['✧', 'Soft Skin-Friendly Fabrics', 'Zero-scratch linings safe for baby skin', 'fabric'],
          ['◷', 'On-Time Event Delivery', 'Delivered right before your puja or party', 'delivery'],
          ['✣', 'Free Fitting Support', 'Includes custom waist & blouse alterations', 'support']
        ].map(([icon, title, text, variant]) => <div className={`trust-item trust-item-${variant}`} key={title}><span className="trust-icon" aria-hidden="true">{icon}</span><div><strong>{title}</strong><small>{text}</small></div></div>)}
      </section>
      <section className="home-section category-section">
        <div className="section-heading"><div><span className="new-kicker">Shop by occasion</span><h2>Find the perfect matching outfit</h2><p>From first birthdays to festive family portraits.</p></div><button className="text-button" onClick={() => navigate('/shop')}>View all matching sets ↗</button></div>
        <div className="category-rail">
          {categoryItems.map((category, index) => {
            const title = typeof category === 'string' ? category : category.name;
            const fallback = categoryImageMap[title] || '/boywear.jpeg';
            const image = typeof category === 'string' ? fallback : imageFor(category, fallback);
            return <button className="category-tile" key={title} onClick={() => typeof category === 'string' ? navigate('/shop') : navigate(`/shop?category=${category._id}`)}><img src={image} alt="" /><strong>{title}</strong><small>Explore the edit</small></button>;
          })}
        </div>
      </section>
      <section className="home-section product-section home-band">
        <div className="section-heading"><div><span className="new-kicker">Fresh from the atelier</span><h2>Our newest arrivals</h2><p>Made to be noticed, remembered, and loved for years.</p></div><button className="text-button" onClick={() => navigate('/shop')}>Shop all pieces ↗</button></div>
        <div className="new-product-grid">{displayedProducts.length ? displayedProducts.map((product) => <article className="new-product-card" key={product._id} onClick={() => navigate(`/product/${product._id}`)}><div className="new-product-image"><img src={imageFor(product, heroImage)} alt={product.name} /><span>{product.isNew ? 'New arrival' : 'Hand finished'}</span><button aria-label={`View ${product.name}`}>♡</button></div><div className="new-product-info"><small>{product.category?.name || 'Occasion wear'}</small><h3>{product.name}</h3><strong>{formatPrice(product.price)}</strong></div></article>) : <div className="empty-state">New pieces are being prepared.</div>}</div>
      </section>
      <section className="bundle-feature home-section">
        <div className="bundle-images">
          <img src="/familycombo.jpeg" alt="Family combo set" />
        </div>
        <div className="bundle-copy">
          <span className="new-kicker">Curated for a shared story</span>
          <h2>Celebrate in style: custom family &amp; duo sets</h2>
          <p>Pick looks for both mom and daughter, or create a coordinated family story with our styling team.</p>
          <button className="new-button" onClick={() => navigate('/contact')}>Create a matching set ↗</button>
          <small>Express dispatch available for select styles</small>
        </div>
      </section>
      <section className="offer-section home-section"><span className="offer-icon">✉</span><span className="new-kicker">A little welcome gift</span><h2>Get ₹500 off your first order</h2><p>Join our family and receive a welcome offer for your first celebration look.</p><button className="new-button" onClick={() => navigate('/register')}>Get the offer ↗</button></section>
      <section className="service-row home-band">{[['♧', 'Personal styling', 'Advice for every occasion'], ['□', 'Carefully made', 'Details worth passing down'], ['◌', 'Worldwide delivery', 'Thoughtful dispatch, wherever you are'], ['♧', 'Milestone dates', 'We help you plan ahead']].map(([icon, title, text]) => <div key={title}><span>{icon}</span><div><strong>{title}</strong><small>{text}</small></div></div>)}</section>
    </div>
  );
}
